import React, { useMemo, useRef, useState } from 'react';
import { InputAdornment, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import SearchIcon from '@mui/icons-material/Search';
import Popper from '@mui/material/Popper';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useLocation } from 'react-router';
import { PopperContent } from '@/components/Hypertracker/HyperSearch/HyperSearchStyled';
import LastSearchList from '@/components/Hypertracker/HyperSearch/LastSearchList';
import { storageKey } from '@/components/Hypertracker/HyperSearch/hypersearchTypes';
import MainButton from '@/components/Hypertracker/MainButton';
import { useUnifiedSearch, MIN_CHARS_FE_LOCAL } from './useUnifiedSearch';
import { useSearchRowNavigate, saveLastSearch } from './useSearchRowNavigate';
import UnifiedSearchDropdown from './UnifiedSearchDropdown';
import { SearchField, SearchWrapper, SubmitButtonContainer } from './UnifiedSearchInputStyled';

interface UnifiedSearchInputProps {
    id: string;
    variant: 'header' | 'homepage';
    isFocusedProp?: boolean;
    isSocketLoading?: boolean;
    isSocketError?: boolean;
    'data-testid'?: string;
}

const UnifiedSearchInput: React.FC<UnifiedSearchInputProps> = ({
    id,
    variant,
    isFocusedProp = false,
    isSocketLoading = false,
    'data-testid': testId,
}) => {
    const isHeader = variant === 'header';
    const { t } = useTranslation('translation', { keyPrefix: 'hypersearch' });
    const { t: tInput } = useTranslation('translation', { keyPrefix: 'input' });
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [isFocused, setIsFocused] = useState<boolean>(isFocusedProp || !isHeader);
    const [searchText, setSearchText] = useState<string>('');
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const listRef = useRef<HTMLUListElement | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const location = useLocation();

    const lastSearch = useMemo(() => {
        const storageData = localStorage.getItem(storageKey);
        return storageData ? JSON.parse(storageData) : null;
    }, [location]);

    const isOpen = searchText.trim().length >= MIN_CHARS_FE_LOCAL;
    const { entries, isLoadingRemote } = useUnifiedSearch(searchText);

    const clearSearchInput = () => {
        setSearchText('');
        setFocusedIndex(-1);

        if (isHeader && !isFocusedProp) {
            setIsFocused(false);
        }
    };

    const { onResultClick } = useSearchRowNavigate(clearSearchInput);

    const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchText(event.target.value);
    };

    const onBlur = (passedRef: React.Ref<HTMLUListElement> | React.Ref<HTMLInputElement>) => {
        setTimeout(() => {
            const ref = passedRef as React.RefObject<HTMLElement>;
            if (ref.current !== document.activeElement) {
                if (isHeader && !isFocusedProp) {
                    setIsFocused(false);
                }
                clearSearchInput();
            }
        }, 200);
    };

    const onFocus = () => setIsFocused(true);

    if (isHeader && isMobile) return null;

    const onSubmitButtonClick = () => {
        const target = entries[focusedIndex] ?? entries[0];
        if (target) onResultClick(target);
    };

    const handleOnKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setFocusedIndex(0);
            listRef.current?.focus();
        }
        if (event.key === 'Escape') {
            event.preventDefault();
            inputRef.current?.blur();
            onBlur(listRef);
        }
        if (event.key === 'Enter' && !isHeader) {
            event.preventDefault();
            onSubmitButtonClick();
        }
    };

    return (
        <SearchWrapper $isHeader={isHeader} $isFocused={isFocused} id={`${id}-wrapper`}>
            <SearchField
                $isHeader={isHeader}
                $isFocused={isFocused}
                inputRef={inputRef}
                onKeyDown={handleOnKeyDown}
                label={null}
                onFocus={onFocus}
                onBlur={() => onBlur(listRef)}
                value={searchText}
                onChange={onInputChange}
                variant={'outlined'}
                id={id}
                fullWidth
                autoFocus={!isHeader}
                slotProps={{
                    input: {
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon
                                    sx={{
                                        height: '16px',
                                        width: 'auto',
                                        path: {
                                            fill:
                                                isFocused || !isHeader
                                                    ? theme.palette.customText.text1
                                                    : theme.palette.common.white,
                                        },
                                    }}
                                />
                            </InputAdornment>
                        ),
                        endAdornment:
                            isHeader && isFocused ? (
                                <InputAdornment position={'end'}>
                                    <LastSearchList
                                        lastSearch={lastSearch}
                                        onSaveSearchClick={saveLastSearch}
                                    />
                                </InputAdornment>
                            ) : null,
                        placeholder: isHeader
                            ? isFocused
                                ? t('search_placeholder')
                                : t('title')
                            : t('search_placeholder'),
                        autoComplete: 'off',
                        autoCapitalize: 'off',
                        autoCorrect: 'off',
                    },
                    htmlInput: {
                        'data-testid':
                            testId ?? (!isHeader ? 'hypertracker-search-input' : undefined),
                    },
                }}
            />
            {!isHeader && (
                <SubmitButtonContainer>
                    <MainButton
                        type="button"
                        onClick={onSubmitButtonClick}
                        data-testid="hypertracker-search-button"
                        sx={{
                            backgroundColor: isSocketLoading
                                ? theme.palette.warning.main
                                : theme.palette.backgrounds.turquoiseBg,
                            color: theme.palette.customText.text6,
                            fontSize: '14px',
                        }}
                    >
                        {isSocketLoading
                            ? tInput('button_text_analysing')
                            : tInput('button_text_analyse')}
                    </MainButton>
                </SubmitButtonContainer>
            )}
            <Popper
                open={isOpen}
                placement="bottom-start"
                anchorEl={isOpen ? document.getElementById(id) : null}
                sx={{
                    zIndex: 100,
                    maxWidth: 'calc(100vw - 32px)',
                    '& .MuiBox-root': {
                        outline: 'none',
                        border: 'none',
                    },
                }}
                modifiers={[
                    {
                        name: 'flip',
                        enabled: false,
                        options: {
                            altBoundary: false,
                            rootBoundary: 'viewport',
                            padding: 8,
                        },
                    },
                    {
                        name: 'preventOverflow',
                        enabled: true,
                        options: {
                            altAxis: true,
                            altBoundary: true,
                            tether: true,
                            rootBoundary: 'viewport',
                            padding: 16,
                        },
                    },
                ]}
            >
                <PopperContent>
                    <UnifiedSearchDropdown
                        entries={entries}
                        searchText={searchText}
                        isLoadingRemote={isLoadingRemote}
                        focusedIndex={focusedIndex}
                        setFocusedIndex={setFocusedIndex}
                        listRef={listRef}
                        inputRef={inputRef}
                        onBlur={onBlur}
                        onResultClick={onResultClick}
                    />
                </PopperContent>
            </Popper>
        </SearchWrapper>
    );
};

export default UnifiedSearchInput;
